import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// PWA: register service worker and manage update lifecycle so existing users
// pick up new deploys without having to manually clear cache.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js", { scope: "/" })
      .then((registration) => {
        // Notify the app when a new SW finishes installing and is waiting.
        const notifyWaiting = (worker) => {
          if (!worker) return;
          window.dispatchEvent(
            new CustomEvent("sw-update-available", {
              detail: {
                activate: () => {
                  try {
                    worker.postMessage({ type: "SKIP_WAITING" });
                  } catch {
                    // Ignore: reload will still be triggered on controllerchange
                  }
                },
              },
            }),
          );
        };

        // Case 1: A new SW is already waiting when the page loads.
        if (registration.waiting && navigator.serviceWorker.controller) {
          notifyWaiting(registration.waiting);
        }

        // Case 2: A new SW is discovered while the tab is open.
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener("statechange", () => {
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              notifyWaiting(newWorker);
            }
          });
        });

        // Periodic + opportunistic update checks: hourly, on focus, on online.
        const doUpdate = () => {
          registration.update().catch(() => {});
        };
        setInterval(doUpdate, 60 * 60 * 1000);
        window.addEventListener("focus", doUpdate);
        window.addEventListener("online", doUpdate);
      })
      .catch(() => {
        // Service worker registration failure is non-fatal; app still works without PWA.
      });

    // When the SW takes over (after SKIP_WAITING), reload once so the page
    // loads the freshly cached assets.
    let hasReloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (hasReloaded) return;
      hasReloaded = true;
      window.location.reload();
    });
  });
}
